import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from '@app/shared/ui/atoms/Common';
import { formatNumber } from '@app/shared/utils/common';
import { Page } from '@app/routes';
import { BalanceType } from '@app/shared/types/pages/friends';
import { Gender } from '@app/shared/types/common';
import { cakeIcon } from '@app/assets';

export interface UserListItemProps {
  isMe: boolean;
  index: number;
  avatarUrl: string;
  userId: number;
  userName: string;
  userActivityTranslationKey?: string | null;
  gender?: Gender;
  balance: number;
  balanceType: BalanceType;
}

const EMOJIS = ['🥇', '🥈', '🥉'];

const BALANCE_TYPE_TO_ICON_MAP: Record<BalanceType, string | React.JSX.Element> = {
  [BalanceType.PANDA]: '🐼',
  [BalanceType.COOKIE]: <img src={cakeIcon} alt="cookie" className="size-4" />,
  [BalanceType.TON]: '💎',
  [BalanceType.XP]: <span className="text-accent">xp</span>,
};

export function UserListItem({
  isMe,
  index,
  avatarUrl,
  userId,
  userName,
  userActivityTranslationKey,
  gender,
  balance,
  balanceType,
}: UserListItemProps) {
  const { t } = useTranslation();

  const renderBalance = () => {
    const formattedBalance =
      balanceType === BalanceType.PANDA ? Math.floor(balance) : formatNumber(balance);
    const icon = BALANCE_TYPE_TO_ICON_MAP[balanceType];

    return (
      <>
        <span className="text-xs font-semibold text-accent">{formattedBalance}</span>
        {typeof icon === 'string' ? <span className="text-xs">{icon}</span> : icon}
      </>
    );
  };

  const profileLink = isMe ? Page.PROFILE : `${Page.PROFILE}/${String(userId)}`;
  const emoji = EMOJIS[index] || index + 1;

  return (
    <Link to={profileLink} className="flex items-center gap-3 px-3.5 py-0.5">
      <span className="min-w-5 text-center text-xs font-medium text-secondary-foreground">
        {emoji}
      </span>

      <UserAvatar avatar={avatarUrl} gender={gender} className="size-[55px]" />

      <div className="flex grow flex-col overflow-hidden">
        <h4 className="truncate text-sm font-medium leading-none text-tertiary-foreground">
          {userName}
        </h4>
        {userActivityTranslationKey && (
          <span className="truncate text-xs font-medium text-tertiary-foreground/60">
            {t(`activities.${userActivityTranslationKey}`)}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">{renderBalance()}</div>
    </Link>
  );
}
